import { toNumber } from '../../utils/numberUtils.js'
import { REGIMENES } from './regimenesEspeciales.js'

function normalizeTag(value) {
  return String(value || '').toLowerCase().trim()
}

/**
 * Map from dropdown/structured regimen values to prorrating internal keys.
 * SPECIAL regimes (edad diferente, aportes diferentes): DOCENTE, INSALUBRE
 * STANDARD regimes (misma edad que GENERAL): COMUN, CONSTRUCCION, AGRARIO, SERVICIO_DOMESTICO -> all map to COMUN
 */
const REGIMEN_KEY_MAP = {
  COMUN: 'COMUN',
  DOCENTE: 'DOCENTE',
  INSALUBRE: 'INSALUBRE',
  CONSTRUCCION: 'COMUN',
  AGRARIO: 'COMUN',
  SERVICIO_DOMESTICO: 'COMUN',
}

function resolveRegimenKey(regimenContributivo) {
  const key = String(regimenContributivo || '').toUpperCase().trim()
  return REGIMEN_KEY_MAP[key] || 'COMUN'
}

function normalizeRegimenKey(value) {
  const txt = String(value || '').toUpperCase().trim()
  if (txt.includes('INSAL')) return 'INSALUBRE'
  if (txt.includes('DOC')) return 'DOCENTE'
  if (txt.includes('PROV') || txt.includes('IPS')) return 'PROVINCIAL'
  return 'GENERAL'
}

function extractManualServicios(expediente = {}) {
  const inRows = Array.isArray(expediente?.prorrateoServicios) ? expediente.prorrateoServicios : []
  return inRows
    .map((r) => ({
      regimen: normalizeRegimenKey(r?.regimen),
      anios: toNumber(r?.anios),
    }))
    .filter((r) => r.anios > 0)
}

function inferRegimenFromRow(row = {}) {
  const bag = [
    row?.tipoActividad,
    row?.tipo,
    row?.regimen,
    row?.caja,
    row?.empresa,
    row?.razonSocial,
  ].map(normalizeTag).join(' ')

  if (/insalubre|chofer|transporte|colectivo|uocra|minero/.test(bag)) {
    return 'INSALUBRE'
  }
  if (/docente|educacion|maestra|profesor/.test(bag)) return 'DOCENTE'
  if (/ips|provincial|caja provincial/.test(bag)) return 'PROVINCIAL'
  return 'GENERAL'
}

function getRowRegimen(row = {}) {
  // Fast path: use structured regimenContributivo from dropdown
  const structured = String(row.regimenContributivo || '').toUpperCase().trim()
  if (structured && REGIMEN_KEY_MAP[structured]) {
    const mapped = resolveRegimenKey(structured)
    if (mapped === 'DOCENTE' || mapped === 'INSALUBRE') return mapped
  }

  // Fallback: existing bag-of-words for rows without dropdown override
  return inferRegimenFromRow(row)
}

export function calcularProrrateoReciproco(expediente = {}, rows = []) {
  const data = Array.isArray(rows) ? rows : []
  const byRegimen = new Map()
  const sexo = String(expediente?.sexo || 'F').toUpperCase() === 'M' ? 'M' : 'F'

  data.forEach((row) => {
    const monto = toNumber(row?.monto ?? row?.remImpSS ?? row?.montoOriginal)
    if (!(monto > 0)) return
    const rawReg = getRowRegimen(row)
    const reg = resolveRegimenKey(rawReg)
    byRegimen.set(reg, (byRegimen.get(reg) || 0) + 1)
  })

  const manualServicios = extractManualServicios(expediente)
  manualServicios.forEach((row) => {
    const meses = Math.round(row.anios * 12)
    const reg = REGIMEN_KEY_MAP[row.regimen] || 'COMUN'
    byRegimen.set(reg, (byRegimen.get(reg) || 0) + meses)
  })

  // Build detalle from the 3 prorrateable groups
  const PRORRATEO_GROUPS = ['DOCENTE', 'INSALUBRE', 'COMUN']
  const detalleRegimenes = PRORRATEO_GROUPS
    .map((key) => {
      const cfg = REGIMENES[key]
      if (!cfg) return null
      const meses = Number(byRegimen.get(key) || 0)
      if (meses === 0) return null
      const anios = meses / 12
      const aportesReq = cfg.aniosAportes || 30
      const fraccion = aportesReq > 0 ? (anios / aportesReq) : 0
      const edadReq = cfg.edadMinima?.[sexo] ?? 65
      return {
        key,
        meses,
        anios: Number(anios.toFixed(2)),
        edadRequerida: edadReq,
        aportesRequeridos: aportesReq,
        fraccion: Number(fraccion.toFixed(4)),
      }
    })
    .filter(Boolean)

  const fraccionTotal = detalleRegimenes.reduce((acc, r) => acc + Number(r.fraccion || 0), 0)
  const cumpleDerechoCausado = fraccionTotal >= 1

  const aporteTotal = detalleRegimenes.reduce((acc, r) => acc + Number(r.anios || 0), 0)
  const edadProrrateadaRaw = detalleRegimenes.reduce((acc, r) => {
    const peso = aporteTotal > 0 ? Number(r.anios || 0) / aporteTotal : 0
    return acc + (peso * Number(r.edadRequerida || 65))
  }, 0)
  const edadProrrateada = Math.max(55, Math.min(65, edadProrrateadaRaw || 65))

  const edadMinimaBase = String(expediente?.sexo || 'F').toUpperCase() === 'F' ? 60 : 65

  return {
    aplica: detalleRegimenes.length > 1,
    regimenesDetectados: detalleRegimenes,
    fracciones: {
      total: Number(fraccionTotal.toFixed(4)),
    },
    derechoCausado: cumpleDerechoCausado,
    edadJubilacionProrrateada: Number(edadProrrateada.toFixed(2)),
    edadMinimaGeneralBase: edadMinimaBase,
    usaServiciosManuales: manualServicios.length > 0,
    formula: 'Art. 18 Dec. 433/94: sum(anios_regimen / anios_requeridos_regimen) >= 1',
  }
}
